import React, { useEffect, useState } from 'react';
import { api } from '../../../services/api';
import { ModuleDefinition } from '../../../types/module';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Power, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';

// Simple Switch component if not found (will replace if find_by_name returns one)
// But to be safe, I'll use a Button approach "Enable/Disable" first or a basic checkbox styled.
// Actually, I'll look at the find_by_name result. If Switch exists, I'll import it.
// For now, I'll write the logic assuming I might need to swap the Toggle component.

const ModuleManagement: React.FC = () => {
    const [modules, setModules] = useState<ModuleDefinition[]>([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState<string | null>(null);

    const loadModules = async () => {
        setLoading(true);
        try {
            const data = await api.system.listModules();
            if (data.modules) {
                setModules(data.modules);
            }
        } catch (error) {
            toast.error("Failed to load modules");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadModules();
    }, []);

    const handleToggle = async (module: ModuleDefinition) => {
        setProcessing(module.id);
        const newState = !module.enabled;

        try {
            // calculated new list
            // We need to know the *current configuration value* to preserve other modules if the API list is partial? 
            // Actually API list returns all installed modules. 
            // So we can reconstruct the full list from state.

            const currentEnabled = modules.filter(m => m.enabled).map(m => m.id);
            let newEnabledIds: string[];

            if (newState) {
                newEnabledIds = [...currentEnabled, module.id];
            } else {
                newEnabledIds = currentEnabled.filter(id => id !== module.id);
            }

            // Deduplicate just in case
            newEnabledIds = Array.from(new Set(newEnabledIds));

            await api.settings.update('enabled_modules', { value: newEnabledIds.join(',') });

            toast.success(`Module ${module.name} ${newState ? 'enabled' : 'disabled'}`);

            // Optimistic update
            setModules(prev => prev.map(m => m.id === module.id ? { ...m, enabled: newState } : m));

            // Optional: Prompt reload
            toast.message('Reload required', {
                description: 'Please reload the page to apply changes.',
                action: {
                    label: 'Reload',
                    onClick: () => window.location.reload()
                }
            });

        } catch (error) {
            toast.error("Failed to update module state");
            console.error(error);
        } finally {
            setProcessing(null);
        }
    };

    return (
        <div className="container mx-auto py-10">
            <h1 className="text-3xl font-bold mb-6">Module Management</h1>

            <Alert className="mb-6 bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Attention</AlertTitle>
                <AlertDescription>
                    Disabling modules will remove their functionality and navigation items immediately.
                    You may need to reload the page to see full changes.
                </AlertDescription>
            </Alert>

            <Card>
                <CardHeader>
                    <CardTitle>Installed Modules</CardTitle>
                    <CardDescription>Manage available system modules and plugins.</CardDescription>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <Loader2 className="animate-spin h-8 w-8 text-primary" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {modules.map(module => (
                                <div key={module.id} className="flex items-center justify-between p-4 border rounded-lg bg-card hover:bg-accent/5 transition-colors">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold">{module.name}</h3>
                                            <span className="text-xs text-muted-foreground px-2 py-0.5 bg-secondary rounded-full">
                                                v{module.version || '1.0.0'}
                                            </span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">{module.description || 'No description provided.'}</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className={`text-sm font-medium ${module.enabled ? 'text-green-500' : 'text-slate-500'}`}>
                                            {module.enabled ? 'Active' : 'Disabled'}
                                        </div>
                                        <Button
                                            variant={module.enabled ? "outline" : "default"}
                                            size="sm"
                                            disabled={!!processing}
                                            onClick={() => handleToggle(module)}
                                            className={module.enabled ? "border-red-500/50 text-red-500 hover:bg-red-500/10" : ""}
                                        >
                                            {processing === module.id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Power className="h-4 w-4 mr-2" />
                                            )}
                                            {module.enabled ? 'Disable' : 'Enable'}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            {modules.length === 0 && (
                                <div className="text-center py-8 text-muted-foreground">
                                    No modules found.
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default ModuleManagement;
